if (!customElements.get('sugarless-carousel')) {
  customElements.define('sugarless-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      this.lifecycle = new AbortController();
      const options = { signal: this.lifecycle.signal };
      this.slides = [...this.querySelectorAll('.sl-carousel__slide')];
      if (this.slides.length < 2) return;
      this.index = 0;
      this.dots = [...this.querySelectorAll('[data-carousel-index]')];
      this.pauseButton = this.querySelector('[data-carousel-pause]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.dataset.autoplay !== 'true' || this.motion.matches || Boolean(window.Shopify?.designMode);
      this.delay = Math.max(3000, Number(this.dataset.interval) || 7000);
      this.inView = true;
      this.slides.forEach(slide => { slide.hidden = false; });
      this.querySelector('.sl-carousel__controls').hidden = false;
      this.querySelector('[data-carousel-previous]')?.addEventListener('click', () => this.select(this.index - 1, true), options);
      this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.select(this.index + 1, true), options);
      this.dots.forEach(dot => dot.addEventListener('click', () => this.select(Number(dot.dataset.carouselIndex), true), options));
      this.pauseButton?.addEventListener('pointerdown', () => { this.pauseIntent = this.paused; }, options);
      this.pauseButton?.addEventListener('click', () => { this.paused = !(this.pauseIntent ?? this.paused); this.pauseIntent = null; this.updatePause(); this.schedule(); }, options);
      // Keyboard focus pauses rotation; pointer navigation keeps autoplay running.
      this.addEventListener('focusin', event => {
        if (event.target.matches(':focus-visible')) {
          this.paused = true;
          this.updatePause();
          this.schedule();
        }
      }, options);
      this.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || event.target.matches('input,textarea,select')) return;
        event.preventDefault();
        this.select(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
      }, options);
      this.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'touch' || !event.target.closest('.sl-carousel__slides')) return;
        this.suppressClick = false;
        this.touchStart = { x: event.clientX, y: event.clientY };
      }, options);
      this.addEventListener('pointerup', event => {
        if (!this.touchStart || event.pointerType !== 'touch') return;
        const x = event.clientX - this.touchStart.x, y = event.clientY - this.touchStart.y;
        this.touchStart = null;
        if (Math.abs(x) > 55 && Math.abs(x) > Math.abs(y) * 1.5) {
          this.suppressClick = true;
          this.select(this.index + (x < 0 ? 1 : -1), true);
        }
      }, options);
      this.addEventListener('click', event => {
        if (this.suppressClick) { event.preventDefault(); event.stopImmediatePropagation(); this.suppressClick = false; }
      }, { ...options, capture: true });
      this.addEventListener('pointercancel', () => { this.touchStart = null; }, options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.motion.addEventListener('change', event => {
        if (event.matches) { this.paused = true; this.updatePause(); }
        this.schedule();
      }, options);
      this.addEventListener('shopify:block:select', event => {
        const slide = event.target.closest('.sl-carousel__slide');
        const index = this.slides.indexOf(slide);
        if (index >= 0) {
          this.paused = true;
          this.select(index, true);
        }
      }, options);
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.schedule();
      }, { threshold: 0.2 });
      this.observer.observe(this);
      this.select(0);
    }
    select(index, manual = false) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.toggleAttribute('data-active', active);
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      const count = this.querySelector('[data-carousel-current]');
      if (count) count.textContent = String(this.index + 1);
      // Start loading the next frame before it is shown.
      this.slides[(this.index + 1) % this.slides.length].querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
      if (manual) {
        this.querySelector('[data-carousel-status]').textContent = `Banner ${this.index + 1} of ${this.slides.length}`;
      }
      this.updatePause();
      this.schedule();
    }
    updatePause() {
      if (!this.pauseButton) return;
      this.pauseButton.setAttribute('aria-label', this.paused ? 'Play automatic banner rotation' : 'Pause automatic banner rotation');
      this.pauseButton.querySelector('[data-carousel-play]').hidden = !this.paused;
      this.pauseButton.querySelector('[data-carousel-pause-icon]').hidden = this.paused;
    }
    schedule() {
      clearTimeout(this.timer);
      if (this.paused || !this.inView || document.hidden) return;
      this.timer = setTimeout(() => this.select(this.index + 1), this.delay);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.lifecycle?.abort();
      this.lifecycle = null;
      this.observer?.disconnect();
    }
  });
}
